import { useCallback } from 'react';

import { inkColor, type InkTone } from '../lib/motion';
import { useMotion } from './useMotion';

export type InkBurst = { id: number; x: number; y: number; color: string };

type Listener = (burst: InkBurst) => void;

// A module-level emitter rather than context state: the layer must not
// re-render the tree that triggered it, and a mutation's onSuccess often fires
// from a component that has already unmounted (a sheet that closed).
const listeners = new Set<Listener>();
let nextId = 1;

export const subscribeToInk = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

// Where the last press landed. Almost every burst belongs to the press that
// started the request, so call sites do not have to thread the event through
// a mutation — they just ask for ink when the server answers.
let lastPointer: { x: number; y: number } | null = null;

export const rememberPointer = (x: number, y: number): void => {
  lastPointer = { x, y };
};

const origin = (event?: { clientX?: number; clientY?: number } | null) => {
  if (event && typeof event.clientX === 'number' && event.clientX > 0) {
    return { x: event.clientX, y: event.clientY ?? window.innerHeight / 2 };
  }
  // Keyboard activation and menu items have no coordinates of their own.
  return lastPointer ?? { x: window.innerWidth / 2, y: window.innerHeight / 2 };
};

const emit = (x: number, y: number, color: string): void => {
  const burst: InkBurst = { id: nextId++, x, y, color };
  listeners.forEach((listener) => listener(burst));
};

/**
 * The app's confirmation gesture: colour spreads from the spot you pressed.
 *
 *   const ink = useInk();
 *   mutate(payload, { onSuccess: () => ink.burst('done') });
 *
 * `burst` spreads from the last press — which is the button that started the
 * request. Use `from(event)` only when the coordinates must be pinned before
 * the pointer can move on (a sheet that closes and re-lays out the page).
 *
 * Nothing is drawn until the server has answered, and nothing is drawn at all
 * unless the user asked for full motion.
 */
export const useInk = () => {
  const { animates } = useMotion();

  const burst = useCallback(
    (tone: InkTone, projectColor?: string | null) => {
      if (!animates) {
        return;
      }
      const { x, y } = origin();
      emit(x, y, inkColor(tone, projectColor));
    },
    [animates],
  );

  const from = useCallback(
    (event?: { clientX?: number; clientY?: number } | null) => {
      const pinned = origin(event);
      return (tone: InkTone, projectColor?: string | null) => {
        if (!animates) {
          return;
        }
        emit(pinned.x, pinned.y, inkColor(tone, projectColor));
      };
    },
    [animates],
  );

  return { burst, from };
};
